import React, {
  useEffect,
  useContext,
  useState,
  useRef,
  useCallback,
} from "react"
import Loader from "../../../../components/ui/loader"
import PayPalCardFields from "../../../../components/PayPal/Private/PayPalCardFields"
import { PaymentRequests } from "../../../../api/app.service"
import { UserCtx } from "../../../../context/userContext"
import { useToast } from "../../../../hooks/useToast"
import { timeStamp } from "console"
import { NotificationCtx } from "../../../../context/notificationContext"

interface Props {
  setShowNewCardModal: any
  onCardAdded: () => void
  setCardSave: any
}

const NewCardModal: React.FC<Props> = ({
  setShowNewCardModal,
  onCardAdded,
  setCardSave,
}) => {
  const { user } = useContext(UserCtx)
  const { toast } = useToast()
  const [isChecked, setIsChecked] = useState(false)
  const [isTermsAgreed, setIsTermsAgreed] = useState<boolean>(false)
  const [isSdkReady, setIsSdkReady] = useState(false)
  const isCheckedRef = useRef(false)
  const clientID = process.env.REACT_APP_PAYPAL_CLIENT_ID
  const merchantID = process.env.REACT_APP_PAYPAL_PAYER_ID

  const createVaultSetupToken = useCallback(
    (data: any, source: "paypal" | "card" | "apple_pay" | "google-pay") => {
      return PaymentRequests.createPaymentToken({
        user_id: parseInt(user.user_id),
        type: source,
      })
        .then((response: any) => response.data)
        .then((order: any) => order.id)
    },
    []
  )

  const onApprove = useCallback(
    async (
      data: any,
      source: "paypal" | "card" | "apple_pay" | "google-pay"
    ) => {
      try {
        await PaymentRequests.savePaymentToken({
          user_id: parseInt(user.user_id),
          token: data,
          is_update: false,
        })

        setCardSave(true)
        toast.success("Your new card has been saved successfully!")
        setShowNewCardModal(false)
      } catch (error: any) {
        toast.error(
          error.response?.data?.error_detail ||
            "Something went wrong while saving your card."
        )
        console.error("error in approve:", error)
      }
    },
    [onCardAdded]
  )

  useEffect(() => {
    const loadPayPalSDK = () => {
      if (document.getElementById("paypal-sdk")) {
        console.log("PayPal SDK already loaded.")
        setIsSdkReady(true)
        return
      }

      const script = document.createElement("script")
      script.id = "paypal-sdk"
      script.src = `https://www.paypal.com/sdk/js?components=buttons,card-fields&client-id=${clientID}&merchant-id=${merchantID}&currency=USD&intent=capture&disable-funding=paylater`
      script.async = true

      script.onload = () => {
        console.log("PayPal SDK loaded successfully.")
        setIsSdkReady(true)
      }

      script.onerror = (err) => {
        console.error("Failed to load PayPal SDK:", err)
      }

      document.body.appendChild(script)
    }

    loadPayPalSDK()
  }, [])

  if (!isSdkReady) {
    return (
      <div className="flex justify-center items-center w-full h-[300px]">
        <Loader />
      </div>
    )
  }

  return (
    <div
      className="fixed w-screen h-screen top-0 left-0 zoee-backdrop-filter 
      z-[9001] flex items-center justify-center"
    >
      <div
        className="relative px-[24px] md:px-[48px] py-[32px] w-[90%] md:w-[640px] 
        bg-white rounded-[16px] main-shadow flex flex-col items-center overflow-y-auto max-h-[85vh]"
      >
        <h1 className="text-black text-[20px] md:text-[24px] font-semibold text-center mb-[8px]">
          Add a New Payment Method
        </h1>

        <p className="text-grayCharcoal text-center text-[14px] mb-[20px] leading-snug max-w-[520px]">
          Save a card to your Zoee account for quick, secure payments in the
          future.
        </p>

        <div className="w-full flex flex-col items-center text-center rounded-lg p-4 -mt-2">
          <div className="mb-[12px] flex flex-col items-center">
            <img
              src="https://www.paypalobjects.com/webstatic/mktg/logo/AM_mc_vs_dc_ae.jpg"
              alt="Payments by PayPal"
              className="h-10 w-auto"
            />
            <p className="text-grayCharcoal text-[12px] mt-1 leading-tight">
              Secured by PayPal — your card details stay private.
            </p>
          </div>
          <p className="text-[13px] mb-[16px] max-w-[360px] leading-snug">
            Please enter the cardholder name and card details below.
          </p>
          <div className="w-full max-w-[420px]">
            <PayPalCardFields
              isCheckedRef={isCheckedRef}
              isChecked={isChecked}
              setIsChecked={setIsChecked}
              onApprove={onApprove}
              createVaultSetupToken={createVaultSetupToken}
              isTermsAgreed={isTermsAgreed}
              setIsTermsAgreed={setIsTermsAgreed}
              isSubx={false}
              activeService=""
              onCancel={() => setShowNewCardModal(false)}
            />
          </div>
        </div>

        <p className="text-graySlate text-[12px] text-center mt-[16px] leading-tight max-w-[460px]">
          By saving this card, you agree to Zoee’s{" "}
          <a
            href="/terms-and-conditions"
            target="_blank"
            className="text-blurple"
          >
            Agreement & Disclosures
          </a>{" "}
          and PayPal’s{" "}
          <a
            href="https://www.paypal.com/us/webapps/mpp/ua/useragreement-full"
            target="_blank"
            className="text-blurple"
          >
            User Agreement
          </a>
          .
        </p>
      </div>
    </div>
  )
}

export default NewCardModal
